import * as React from 'react'
import styled from 'styled-components'
import InfiniteScroll from 'react-infinite-scroller'
import { LeaderboardTime } from '.'
import { useInfiniteLoadIndexer } from '../../hooks/infiniteLoadIndexer'
import { CREATOR_LEADERBOARD_QUERY } from '../../graphql/graphql-queries'
import { SmallSpinnerContainer, SmallSpinner } from '../spinner'
import { CreatorLeaderboard, OrderBy, Query_Root } from '../../graphql/indexer-graphql-types'
import { CreatorLeaderboardUser } from '../../interfaces/User'
import { fbEnrichUsersFromIds } from '../../data/FirebaseEnrich'
import CreatorRow from './rows/CreatorRow'
import CreatorSkeletonItem from './rows/CreatorSkeletonRow'
import CreatorHeader from './headers/CreatorHeader'
import { tokenOwnerComparer } from '../../utils/comparer'

const itemsContainerStyle = `
  display: flex;
  flex-wrap: wrap;
  position: relative;
  z-index: 0;
  width: 100%;

  // large desktop
  @media (min-width: 82em) {
    max-width: 1800px;
  }
`

const StyledSkeletonContainer = styled.div`
  ${itemsContainerStyle}
`

const StyledInfiniteScroll = styled(InfiniteScroll)`
  ${itemsContainerStyle}
`

const StyledTable = styled.table`
  width: 100%;
  border: none;
  border-collapse: separate;
  border-spacing: 0 15px;
  // large desktop
  @media (min-width: 82em) {
    max-width: 1800px;
  }
`

interface Props {
  time: LeaderboardTime
  currentIndex: number
  index: number
  updateHeight: () => void
}

const parseItems = (data: Query_Root | undefined): CreatorLeaderboard[] => {
  if (!data) {
    return []
  }
  return data.creatorLeaderboard?.filter(Boolean)?.filter(({ tokenOwner }) => !!tokenOwner) || []
}

const enrichItems = async (
  leaderboardItems: CreatorLeaderboard[]
): Promise<CreatorLeaderboardUser[]> => {
  const userIDs = leaderboardItems.map(({ tokenOwner }) => tokenOwner) as string[] | []
  if (!userIDs.length) {
    return []
  }
  const creators = await fbEnrichUsersFromIds(userIDs)
  return userIDs.map((id) => {
    const creator = creators.find((creator) => creator.address === id)
    const leaderboardData = leaderboardItems.find((data) => data.tokenOwner === id)

    return {
      id,
      user: creator,
      leaderboardInfo: leaderboardData,
    }
  })
}

export enum CreatorSelectedFilter {
  COLLECTORS,
  PRIMARY_SALES,
  SECONDARY_SALES,
  TOTAL_SALES,
}

const TrendingCreators = ({ currentIndex, index, updateHeight, time }: Props): JSX.Element => {
  const [selected, setSelected] = React.useState<CreatorSelectedFilter>(
    CreatorSelectedFilter.TOTAL_SALES
  )

  const {
    rawData: originalItems,
    data: items,
    fetchNext,
    isLoading,
    hasNext,
    refetch,
  } = useInfiniteLoadIndexer<CreatorLeaderboardUser, string>({
    id: '',
    query: CREATOR_LEADERBOARD_QUERY,
    enrichItems,
    parseItems,
    comparer: tokenOwnerComparer,
    page_size: 10,
    refetchMarker: 'trendingCreators',
    variables: {
      orderBy:
        time === LeaderboardTime.ALL
          ? selected === CreatorSelectedFilter.COLLECTORS
            ? [{ allCollectors: OrderBy.Desc }]
            : selected === CreatorSelectedFilter.PRIMARY_SALES
            ? [{ allPrimarySalesSol: OrderBy.Desc }]
            : selected === CreatorSelectedFilter.SECONDARY_SALES
            ? [{ allSecondarySalesSol: OrderBy.Desc }]
            : [{ allSalesSol: OrderBy.Desc }]
          : time === LeaderboardTime.MONTH
          ? selected === CreatorSelectedFilter.COLLECTORS
            ? [{ monthCollectors: OrderBy.Desc }]
            : selected === CreatorSelectedFilter.PRIMARY_SALES
            ? [{ monthPrimarySalesSol: OrderBy.Desc }]
            : selected === CreatorSelectedFilter.SECONDARY_SALES
            ? [{ monthSecondarySalesSol: OrderBy.Desc }]
            : [{ monthSalesSol: OrderBy.Desc }]
          : selected === CreatorSelectedFilter.COLLECTORS
          ? [{ weekCollectors: OrderBy.Desc }]
          : selected === CreatorSelectedFilter.PRIMARY_SALES
          ? [{ weekPrimarySalesSol: OrderBy.Desc }]
          : selected === CreatorSelectedFilter.SECONDARY_SALES
          ? [{ weekSecondarySalesSol: OrderBy.Desc }]
          : [{ weekSalesSol: OrderBy.Desc }],
    },
  })

  React.useEffect(() => {
    if (index === currentIndex) {
      refetch()
    }
  }, [currentIndex, index, time, selected])

  React.useEffect(() => {
    if (index === currentIndex && !isLoading) {
      updateHeight()
    }
  }, [items])

  if ((originalItems.length === 0 && hasNext) || isLoading) {
    return (
      <StyledSkeletonContainer>
        <StyledTable>
          <CreatorHeader selected={selected} setSelected={setSelected} />
          <tbody>
            {[...Array(10).keys()].map((index) => (
              <CreatorSkeletonItem rank={index + 1} key={`skeleton-item-${index}`} />
            ))}
          </tbody>
        </StyledTable>
      </StyledSkeletonContainer>
    )
  }

  if (items.length === 0) {
    return <></>
  }

  return (
    <StyledInfiniteScroll
      loadMore={fetchNext}
      hasMore={hasNext}
      loader={
        <SmallSpinnerContainer>
          <SmallSpinner />
        </SmallSpinnerContainer>
      }
      useWindow
    >
      <StyledTable>
        <CreatorHeader selected={selected} setSelected={setSelected} />
        <tbody>
          {items.map(
            (item: CreatorLeaderboardUser, index: number): JSX.Element => (
              <CreatorRow
                time={time}
                filter={selected}
                rank={index + 1}
                {...item}
                key={`list-item-${index}`}
              />
            )
          )}
        </tbody>
      </StyledTable>
    </StyledInfiniteScroll>
  )
}

export default TrendingCreators
